import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  X,
  Image as ImageIcon,
  User,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Clock,
  Camera,
  RefreshCw,
  Loader2,
  Zap,
  Eye
} from 'lucide-react';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { useAuth } from '../../contexts/AuthContext';
import { useCreateSlipSubmission } from '../../hooks/useFirebaseData';
import { uploadFile, functions } from '../../config/firebase';
import { TestModeWarning } from '../common/TestModeBanner';
import { httpsCallable } from 'firebase/functions';

interface TestUploadModalProps {
  onClose: () => void;
}

interface QualityIssue {
  code: string;
  severity: 'error' | 'warning' | 'info';
  title: string;
  message: string;
  suggestion: string;
}

interface ProcessingResult {
  success: boolean;
  submissionId: string;
  status: string;
  confidence: number;
  qualityScore: number;
  issues: QualityIssue[];
  extraction?: {
    sportsbook: string | null;
    betType: string | null;
    totalOdds: string | null;
    wagerAmount: string | null;
    potentialPayout: string | null;
    picksCount: number;
  };
  userMessage: {
    title: string;
    message: string;
    icon: string;
    color: string;
    canRetry: boolean;
  };
  canProceed: boolean;
  shouldRetake: boolean;
}

type UploadState = 'idle' | 'uploading' | 'processing' | 'success' | 'warning' | 'error';

export const TestUploadModal: React.FC<TestUploadModalProps> = ({ onClose }) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [customUsername, setCustomUsername] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [processingResult, setProcessingResult] = useState<ProcessingResult | null>(null);
  const [pollCount, setPollCount] = useState(0);

  const createSubmission = useCreateSlipSubmission();

  // Poll for processing status
  const pollStatus = useCallback(async (subId: string) => {
    if (!subId) return;

    try {
      const getSubmissionStatus = httpsCallable(functions, 'getSubmissionStatus');
      const result = await getSubmissionStatus({ submissionId: subId, isTestMode: true });
      const data = result.data as { isProcessed: boolean; processingResult?: ProcessingResult; status: string };

      if (data.isProcessed && data.processingResult) {
        setProcessingResult(data.processingResult);

        // Determine final state based on result
        if (data.processingResult.shouldRetake) {
          setUploadState('error');
        } else if (data.processingResult.canProceed && data.processingResult.confidence >= 70) {
          setUploadState('success');
        } else if (data.processingResult.canProceed) {
          setUploadState('warning');
        } else {
          setUploadState('error');
        }
        return true; // Done polling
      }

      return false; // Keep polling
    } catch (error) {
      console.error('Error polling status:', error);
      return false;
    }
  }, []);

  // Polling effect
  useEffect(() => {
    if (uploadState !== 'processing' || !submissionId) return;

    const pollInterval = setInterval(async () => {
      setPollCount(prev => prev + 1);
      const done = await pollStatus(submissionId);
      if (done || pollCount >= 30) { // Max 30 polls (30 seconds)
        clearInterval(pollInterval);
        if (pollCount >= 30 && !processingResult) {
          setUploadState('warning');
          setProcessingResult({
            success: true,
            submissionId,
            status: 'pending',
            confidence: 0,
            qualityScore: 0,
            issues: [],
            userMessage: {
              title: 'Processing Taking Longer',
              message: 'Your slip is still being analyzed. Check the verification queue for results.',
              icon: 'clock',
              color: 'warning',
              canRetry: false,
            },
            canProceed: true,
            shouldRetake: false,
          });
        }
      }
    }, 1000);

    return () => clearInterval(pollInterval);
  }, [uploadState, submissionId, pollCount, pollStatus, processingResult]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setErrorMessage('Please select an image file');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        setErrorMessage('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setErrorMessage('');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
      setErrorMessage('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !user) return;

    setIsUploading(true);
    setUploadState('uploading');
    setErrorMessage('');
    setPollCount(0);
    setProcessingResult(null);

    try {
      const timestamp = Date.now();
      const filename = `${timestamp}_test_${selectedFile.name.replace(/[^a-zA-Z0-9.]/g, '_')}`;
      const storagePath = `slip-submissions/${user.uid}/${filename}`;

      // Upload to Firebase Storage
      const imageURL = await uploadFile(storagePath, selectedFile);

      // Create submission record
      const result = await createSubmission.mutateAsync({
        userId: user.uid,
        username: customUsername.trim() || user.username,
        userPhotoURL: user.photoURL || '',
        imageURL,
        source: 'test'
      });

      setSubmissionId(result.submissionId);
      setUploadState('processing');

    } catch (error) {
      console.error('Upload error:', error);
      setUploadState('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to upload slip');
    } finally {
      setIsUploading(false);
    }
  };

  const resetUpload = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setUploadState('idle');
    setErrorMessage('');
    setSubmissionId(null);
    setProcessingResult(null);
    setPollCount(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusIcon = () => {
    const iconMap: Record<string, React.ReactNode> = {
      clock: <Clock size={40} className="text-warning" />,
      check: <CheckCircle size={40} className="text-success" />,
      alert: <AlertTriangle size={40} className="text-orange-500" />,
      camera: <Camera size={40} className="text-error" />,
      x: <X size={40} className="text-error" />,
      refresh: <RefreshCw size={40} className="text-primary" />,
    };
    return iconMap[processingResult?.userMessage?.icon || 'clock'] || <Clock size={40} />;
  };

  const getStatusBgColor = () => {
    const colorMap: Record<string, string> = {
      success: 'bg-success/10',
      warning: 'bg-warning/10',
      error: 'bg-error/10',
      orange: 'bg-orange-500/10',
      info: 'bg-primary/10',
    };
    return colorMap[processingResult?.userMessage?.color || 'info'] || 'bg-primary/10';
  };

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white dark:bg-surface-dark rounded-xl shadow-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Upload size={20} className="text-primary" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Test Upload</h3>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Simulate a user slip upload
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-light dark:hover:bg-gray-800 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <TestModeWarning action="Creating a test submission" />

        {/* Upload Area - Idle/Uploading State */}
        {(uploadState === 'idle' || uploadState === 'uploading') && (
          <>
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                selectedFile
                  ? 'border-primary bg-primary/5'
                  : 'border-border-light dark:border-border-dark hover:border-primary'
              }`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {previewUrl ? (
                <div className="space-y-4">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="max-h-64 mx-auto rounded-lg shadow-md"
                  />
                  <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    {selectedFile?.name}
                  </p>
                  <Button variant="secondary" size="sm" onClick={resetUpload}>
                    Choose Different Image
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="w-16 h-16 bg-surface-light dark:bg-gray-800 rounded-full mx-auto flex items-center justify-center">
                    <ImageIcon size={32} className="text-text-secondary-light dark:text-text-secondary-dark" />
                  </div>
                  <div>
                    <p className="font-medium mb-1">Drop your slip image here</p>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                      or click to browse
                    </p>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Select Image
                  </Button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>

            {/* Optional Username Override */}
            <div className="mt-4">
              <Input
                label="Username (optional)"
                placeholder={user?.username || 'Leave blank to use your username'}
                value={customUsername}
                onChange={(e) => setCustomUsername(e.target.value)}
                icon={<User size={18} />}
                hint="Override the username for testing different users"
              />
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg flex items-center gap-2 text-error text-sm">
                <AlertCircle size={18} />
                {errorMessage}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 mt-6">
              <Button variant="secondary" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                loading={isUploading}
                icon={<Upload size={18} />}
                className="flex-1"
              >
                {isUploading ? 'Uploading...' : 'Upload Slip'}
              </Button>
            </div>
          </>
        )}

        {/* Processing State */}
        {uploadState === 'processing' && (
          <div className="py-12 text-center">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-20 h-20 bg-primary/10 rounded-full mx-auto flex items-center justify-center mb-4"
            >
              <Loader2 size={40} className="text-primary" />
            </motion.div>
            <h4 className="text-lg font-bold mb-2">Analyzing Slip...</h4>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4">
              Our AI is reading your betting slip
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-text-secondary-light dark:text-text-secondary-dark">
              <Zap size={14} className="text-primary" />
              <span>This usually takes 5-10 seconds</span>
            </div>
          </div>
        )}

        {/* Result States */}
        {(uploadState === 'success' || uploadState === 'warning' || uploadState === 'error') && processingResult && (
          <div className="py-8">
            {/* Status Icon and Message */}
            <div className="text-center mb-6">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`w-20 h-20 ${getStatusBgColor()} rounded-full mx-auto flex items-center justify-center mb-4`}
              >
                {getStatusIcon()}
              </motion.div>
              <h4 className="text-lg font-bold mb-2">{processingResult.userMessage.title}</h4>
              <p className="text-text-secondary-light dark:text-text-secondary-dark">
                {processingResult.userMessage.message}
              </p>
            </div>

            {/* Quality Issues */}
            <AnimatePresence>
              {processingResult.issues && processingResult.issues.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 space-y-2"
                >
                  <p className="text-sm font-medium mb-2">Issues Detected:</p>
                  {processingResult.issues.map((issue, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border text-sm ${
                        issue.severity === 'error'
                          ? 'bg-error/5 border-error/20'
                          : issue.severity === 'warning'
                          ? 'bg-warning/5 border-warning/20'
                          : 'bg-primary/5 border-primary/20'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        {issue.severity === 'error' ? (
                          <AlertCircle size={16} className="text-error mt-0.5 flex-shrink-0" />
                        ) : issue.severity === 'warning' ? (
                          <AlertTriangle size={16} className="text-warning mt-0.5 flex-shrink-0" />
                        ) : (
                          <Eye size={16} className="text-primary mt-0.5 flex-shrink-0" />
                        )}
                        <div>
                          <p className="font-medium">{issue.title}</p>
                          <p className="text-text-secondary-light dark:text-text-secondary-dark mt-1">
                            {issue.suggestion}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Extraction Preview (for successful extractions) */}
            {processingResult.extraction && processingResult.confidence >= 40 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-surface-light dark:bg-gray-800 rounded-lg"
              >
                <p className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Zap size={14} className="text-primary" />
                  AI Extraction Preview
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {processingResult.extraction.sportsbook && (
                    <div>
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">Sportsbook:</span>
                      <span className="ml-2 font-medium">{processingResult.extraction.sportsbook}</span>
                    </div>
                  )}
                  {processingResult.extraction.betType && (
                    <div>
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">Bet Type:</span>
                      <span className="ml-2 font-medium">{processingResult.extraction.betType}</span>
                    </div>
                  )}
                  {processingResult.extraction.totalOdds && (
                    <div>
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">Odds:</span>
                      <span className="ml-2 font-medium">{processingResult.extraction.totalOdds}</span>
                    </div>
                  )}
                  {processingResult.extraction.wagerAmount && (
                    <div>
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">Wager:</span>
                      <span className="ml-2 font-medium">{processingResult.extraction.wagerAmount}</span>
                    </div>
                  )}
                  {processingResult.extraction.picksCount > 0 && (
                    <div>
                      <span className="text-text-secondary-light dark:text-text-secondary-dark">Picks:</span>
                      <span className="ml-2 font-medium">{processingResult.extraction.picksCount} detected</span>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        processingResult.confidence >= 70
                          ? 'bg-success'
                          : processingResult.confidence >= 40
                          ? 'bg-warning'
                          : 'bg-error'
                      }`}
                      style={{ width: `${processingResult.confidence}%` }}
                    />
                  </div>
                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                    {processingResult.confidence}% confidence
                  </span>
                </div>
              </motion.div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              {processingResult.userMessage.canRetry && (
                <Button
                  variant="secondary"
                  onClick={resetUpload}
                  icon={<RefreshCw size={18} />}
                  className="flex-1"
                >
                  Try Again
                </Button>
              )}
              <Button
                variant={processingResult.canProceed ? 'primary' : 'secondary'}
                onClick={onClose}
                className="flex-1"
              >
                {processingResult.canProceed ? 'Done' : 'Close'}
              </Button>
            </div>
          </div>
        )}

        {/* Error State (upload failed before processing) */}
        {uploadState === 'error' && !processingResult && (
          <div className="py-12 text-center">
            <div className="w-20 h-20 bg-error/10 rounded-full mx-auto flex items-center justify-center mb-4">
              <AlertCircle size={40} className="text-error" />
            </div>
            <h4 className="text-lg font-bold mb-2">Upload Failed</h4>
            <p className="text-text-secondary-light dark:text-text-secondary-dark mb-4">
              {errorMessage || 'Something went wrong. Please try again.'}
            </p>
            <Button variant="secondary" onClick={resetUpload}>
              Try Again
            </Button>
          </div>
        )}
      </motion.div>
    </motion.div>,
    document.body
  );
};
